import {
  DollarSign,
  TrendingUp,
  Users,
  Repeat,
  UserMinus,
  Percent,
} from 'lucide-react';
import { StripeRevenueData } from './RevenueBreakdown';

interface Client {
  id: string;
  plan: string;
  plan_price: number;
  amount_paid?: number | null;
  status: string;
  paid: boolean;
  has_access?: boolean | null;
  subscription_start: string;
  subscription_end: string;
  created_at: string;
  email: string;
}

interface OwnerKPIsProps {
  clients: Client[];
  stripeData: StripeRevenueData | null;
  periodAiCost: number;
}

const OwnerKPIs = ({ clients, stripeData, periodAiCost }: OwnerKPIsProps) => {
  const isTestAccount = (c: Client) => {
    const email = c.email.toLowerCase();
    return email.includes('test') || email.includes('example') || c.plan.toLowerCase().includes('test');
  };

  const realClients = clients.filter((c) => !isTestAccount(c));
  const now = new Date();

  // All financial data from Stripe
  const mrr = stripeData?.mrr || 0;
  const activeSubCount = stripeData?.activeSubscriptions || 0;
  const periodRevenue = stripeData?.totalPeriodIncome || 0;
  const periodRefunds = stripeData?.totalRefunds || 0;
  const netProfit = periodRevenue - periodAiCost - periodRefunds;

  // Active lifetime from local DB (Stripe doesn't track these as subscriptions)
  const activeLifetime = realClients.filter(
    (c) =>
      c.paid &&
      c.plan.toLowerCase().includes('lifetime') &&
      (c.status === 'active' || c.status === 'expiring'),
  ).length;
  const activePayingMembers = activeSubCount + activeLifetime;

  // Churn: monthly clients who expired
  const allMonthly = realClients.filter((c) => c.plan.toLowerCase() === 'monthly' && c.paid);
  const churned = allMonthly.filter(
    (c) => c.status === 'expired' || new Date(c.subscription_end) < now,
  );
  const churnRate = allMonthly.length > 0 ? (churned.length / allMonthly.length) * 100 : 0;

  // Conversion: freemium → paid
  const freemiumClients = realClients.filter((c) => c.plan.toLowerCase() === 'freemium');
  const convertedClients = realClients.filter((c) => c.paid && c.amount_paid && c.amount_paid > 0);
  const totalLeads = freemiumClients.length + convertedClients.length;
  const conversionRate = totalLeads > 0 ? (convertedClients.length / totalLeads) * 100 : 0;

  const fmt = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const kpis = [
    {
      label: 'MRR',
      value: `$${fmt(mrr)}`,
      subtitle: `${activeSubCount} active subs`,
      icon: Repeat,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      label: 'Period Revenue',
      value: `$${fmt(periodRevenue)}`,
      subtitle: 'Live from Stripe',
      icon: DollarSign,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
    },
    {
      label: 'Net Profit',
      value: `$${fmt(netProfit)}`,
      subtitle: 'Revenue − costs − refunds',
      icon: TrendingUp,
      iconBg: netProfit >= 0 ? 'bg-emerald-100' : 'bg-red-100',
      iconColor: netProfit >= 0 ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'Active Members',
      value: activePayingMembers.toString(),
      subtitle: `${activeSubCount} monthly · ${activeLifetime} lifetime`,
      icon: Users,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      label: 'Churn Rate',
      value: `${churnRate.toFixed(1)}%`,
      subtitle: `${churned.length}/${allMonthly.length} monthly`,
      icon: UserMinus,
      iconBg: churnRate > 10 ? 'bg-red-100' : 'bg-green-100',
      iconColor: churnRate > 10 ? 'text-red-600' : 'text-green-600',
    },
    {
      label: 'Conversion',
      value: `${conversionRate.toFixed(1)}%`,
      subtitle: `${convertedClients.length} paid / ${totalLeads} leads`,
      icon: Percent,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
      {kpis.map((kpi, i) => (
        <div key={i} className="p-4 bg-white border border-border rounded-2xl shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-9 h-9 rounded-xl ${kpi.iconBg} flex items-center justify-center`}>
              <kpi.icon className={`w-4 h-4 ${kpi.iconColor}`} />
            </div>
          </div>
          <p className="text-xl sm:text-2xl font-bold text-foreground">{kpi.value}</p>
          <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">{kpi.label}</p>
          <p className="text-[9px] sm:text-[10px] text-muted-foreground/70 mt-0.5">{kpi.subtitle}</p>
        </div>
      ))}
    </div>
  );
};

export default OwnerKPIs;
